import { isPlatformServer } from '@angular/common';
import { Component, computed, effect, inject, PLATFORM_ID, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import { Meta, Title } from '@angular/platform-browser';
import { catchError, forkJoin, map, of } from 'rxjs';
import type { TestimonialsListingPageData } from '../../../shared/models/marketing.models';
import { MarketingFooterComponent } from '../../components/marketing-footer.component';
import { MarketingHeaderComponent } from '../../components/marketing-header.component';
import { MarketingConsultationCtaComponent } from '../../components/marketing-consultation-cta.component';
import { SuccessStoriesFilterBarComponent } from '../../components/success-stories/success-stories-filter-bar.component';
import { SuccessStoriesGridComponent } from '../../components/success-stories/success-stories-grid.component';
import { SuccessStoriesHeroComponent } from '../../components/success-stories/success-stories-hero.component';
import { SuccessStoriesLoadMoreComponent } from '../../components/success-stories/success-stories-load-more.component';
import {
  heroStatsFromHome,
  matchesCategory,
  matchesSearch,
  STORIES_PAGE_SIZE,
  type StoryCategoryId,
} from '../../components/success-stories/success-stories.utils';
import {
  mapTestimonialCards,
  TESTIMONIALS_SECTION_DEFAULTS,
} from '../../components/testimonials/testimonials.utils';
import { buildPageTitle } from '../../../shared/constants/site-meta';
import { PagesService } from '../../../shared/services/pages.service';

export type TestimonialsAllLoadState =
  | { kind: 'pending' }
  | { kind: 'error' }
  | { kind: 'ok'; page: TestimonialsListingPageData; homeStats: Array<{ value: string; label: string }> };

@Component({
  selector: 'app-marketing-testimonials-all-page',
  standalone: true,
  imports: [
    RouterLink,
    MarketingHeaderComponent,
    MarketingFooterComponent,
    SuccessStoriesHeroComponent,
    SuccessStoriesFilterBarComponent,
    SuccessStoriesGridComponent,
    SuccessStoriesLoadMoreComponent,
    MarketingConsultationCtaComponent,
  ],
  templateUrl: './testimonials-all-page.component.html',
})
export class MarketingTestimonialsAllPageComponent {
  private readonly pages = inject(PagesService);
  private readonly title = inject(Title);
  private readonly meta = inject(Meta);
  private readonly platformId = inject(PLATFORM_ID);

  readonly activeCategory = signal<StoryCategoryId>('all');
  readonly searchQuery = signal('');
  readonly visibleCount = signal(STORIES_PAGE_SIZE);
  readonly loadingMore = signal(false);

  readonly pageState = toSignal(
    forkJoin({
      page: this.pages.getMarketingTestimonialsListing(),
      home: this.pages.getMarketingHome().pipe(catchError(() => of(null))),
    }).pipe(
      map(({ page, home }) => ({
        kind: 'ok' as const,
        page,
        homeStats: home?.stats ?? [],
      })),
      catchError(() =>
        isPlatformServer(this.platformId)
          ? of({ kind: 'pending' as const })
          : of({ kind: 'error' as const }),
      ),
    ),
    { initialValue: { kind: 'pending' as const } satisfies TestimonialsAllLoadState },
  );

  readonly allTestimonials = computed(() => {
    const state = this.pageState();
    if (state.kind !== 'ok') return [];
    return mapTestimonialCards(state.page.items);
  });

  readonly filteredTestimonials = computed(() => {
    const category = this.activeCategory();
    const query = this.searchQuery();
    return this.allTestimonials().filter(
      (item) => matchesCategory(item, category) && matchesSearch(item, query),
    );
  });

  readonly visibleTestimonials = computed(() =>
    this.filteredTestimonials().slice(0, this.visibleCount()),
  );

  readonly hasMore = computed(() => this.visibleCount() < this.filteredTestimonials().length);

  readonly hasAnyTestimonials = computed(() => this.allTestimonials().length > 0);

  readonly heroStats = computed(() => {
    const state = this.pageState();
    if (state.kind !== 'ok') return heroStatsFromHome();
    return heroStatsFromHome(state.homeStats);
  });

  constructor() {
    effect(() => {
      const state = this.pageState();
      if (state.kind === 'ok') {
        this.applySeo();
      }
    });

    effect(() => {
      this.activeCategory();
      this.searchQuery();
      this.visibleCount.set(STORIES_PAGE_SIZE);
    });
  }

  setCategory(category: StoryCategoryId): void {
    this.activeCategory.set(category);
  }

  setSearch(query: string): void {
    this.searchQuery.set(query);
  }

  onLoadMore(): void {
    if (!this.hasMore() || this.loadingMore()) return;
    this.loadingMore.set(true);
    queueMicrotask(() => {
      this.visibleCount.update((count) => count + STORIES_PAGE_SIZE);
      this.loadingMore.set(false);
    });
  }

  private applySeo(): void {
    const state = this.pageState();
    if (state.kind !== 'ok') return;
    const sectionTitle = state.page.title?.trim() || TESTIMONIALS_SECTION_DEFAULTS.title;
    const pageSubtitle =
      state.page.subtitle?.trim() ||
      'Browse every published review from students who achieved their global education goals with Career Grad Global.';
    this.title.setTitle(buildPageTitle(`${sectionTitle} — All Testimonials`));
    this.meta.updateTag({ name: 'description', content: pageSubtitle });
  }
}
